import { useEffect, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Product, PriceFormula } from '@/types';
import { useCreateProduct, useUpdateProduct, useCategories, useCreateCategory } from '@/hooks/useProducts';
import { useSuppliers } from '@/hooks/useSuppliers';
import { productsService } from '@/services/products';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import { useBarcodeScanner } from '@/hooks/useBarcodeScanner';
import { useAuthStore } from '@/context/authStore';
import Select from '@/components/ui/Select';
import { Calculator } from 'lucide-react';
import toast from 'react-hot-toast';

const SPECIES_OPTIONS = [
  { value: 'Canino', label: 'Canino' },
  { value: 'Felino', label: 'Felino' },
  { value: 'Otro', label: 'Otro' },
];

// Funciones para calcular precios sugeridos según la fórmula de la categoría
const calculateSalePrice = (
  costPrice: number,
  formula: PriceFormula = 'multiply_round_up',
  multiplier: number = 2.0,
  marginPercentage: number = 0,
  roundTo: number = 100
): number => {
  let result: number;

  switch (formula) {
    case 'multiply_round_up':
      // MULTIPLO.SUPERIOR(costo * multiplicador, roundTo)
      result = costPrice * multiplier;
      return Math.ceil(result / roundTo) * roundTo;
    case 'multiply_exact':
      // Multiplicación exacta sin redondeo
      result = costPrice * multiplier;
      return Math.round(result * 100) / 100;
    case 'fixed_margin':
      // Costo + margen fijo (el multiplier representa el margen)
      return Math.round((costPrice + multiplier) * 100) / 100;
    case 'margin_percentage':
      // Precio = Costo + margen%
      // Ej: costo 100, margen 30% → 100 * 1.30 = 130
      result = costPrice * (1 + marginPercentage / 100);
      return roundTo > 0 ? Math.ceil(result / roundTo) * roundTo : Math.round(result * 100) / 100;
    default:
      result = costPrice * multiplier;
      return Math.ceil(result / roundTo) * roundTo;
  }
};

// Descripción de la fórmula para mostrar al usuario
const getFormulaDescription = (
  formula: PriceFormula = 'multiply_round_up',
  multiplier: number = 2.0,
  marginPercentage: number = 0,
  roundTo: number = 100
): string => {
  switch (formula) {
    case 'multiply_round_up':
      return `Costo × ${multiplier} (redondeado a ${roundTo})`;
    case 'multiply_exact':
      return `Costo × ${multiplier}`;
    case 'fixed_margin':
      return `Costo + $${multiplier.toLocaleString('es-AR')}`;
    case 'margin_percentage':
      return `Margen ${marginPercentage}%`;
    default:
      return `Costo × ${multiplier}`;
  }
};

// Precio tarjeta = precio efectivo * multiplicador, redondeado hacia arriba al múltiplo de 100
const calculateListPrice = (salePrice: number, multiplier: number = 1.10, roundTo: number = 100): number => {
  const rawPrice = salePrice * multiplier;
  return Math.ceil(rawPrice / roundTo) * roundTo;
};

// --- Formato de precios con separador de miles (es-AR) ---
// El form guarda el valor "canónico" (ej. "13231.5"); acá solo formateamos
// para mostrar (ej. "13.231,5") y parseamos lo que escribe el usuario.
const formatMiles = (canonical: string | undefined): string => {
  if (!canonical) return '';
  const [intPartRaw, decPart] = canonical.split('.');
  const intPart = (intPartRaw || '').replace(/^0+(?=\d)/, '') || '0';
  const withDots = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  return decPart != null ? `${withDots},${decPart}` : withDots;
};

const parseToCanonical = (display: string): string => {
  // Quitar puntos de miles, pasar coma decimal a punto, dejar solo dígitos y un punto
  let s = display.replace(/\./g, '').replace(',', '.').replace(/[^\d.]/g, '');
  const firstDot = s.indexOf('.');
  if (firstDot !== -1) {
    s = s.slice(0, firstDot + 1) + s.slice(firstDot + 1).replace(/\./g, '');
  }
  return s;
};

const productSchema = z.object({
  name: z.string().min(1, 'El nombre es requerido'),
  code: z.string().optional(),
  barcode: z.string().optional(),
  description: z.string().optional(),
  category_id: z.string().optional(),
  supplier_id: z.string().optional(),
  sale_price: z.string().optional(),  // Precio efectivo/transferencia (opcional para servicios con precio variable)
  list_price: z.string().optional(),  // Precio de lista (tarjeta)
  installment_price: z.string().optional(),  // Precio crédito en cuotas (opcional)
  cost_price: z.string().optional(),
  current_stock: z.string().default('0'),
  minimum_stock: z.string().default('10'),
  track_stock: z.boolean().default(true),
  expiration_date: z.string().optional(),
  batch_number: z.string().optional(),
  is_service: z.boolean().default(false),
  applicable_species: z.array(z.string()).default([]),
  // Venta por ml
  sell_by_ml: z.boolean().default(false),
  container_size_ml: z.string().optional(),
  price_per_ml: z.string().optional(),
  // Producto a granel (se puede fraccionar)
  is_bulk: z.boolean().default(false),
});

type ProductFormData = z.infer<typeof productSchema>;

interface ProductFormProps {
  product?: Product | null;
  onSuccess: () => void;
}

export default function ProductForm({ product, onSuccess }: ProductFormProps) {
  const { data: categories } = useCategories();
  const { data: suppliers } = useSuppliers();
  // Solo mostrar el precio de "crédito en cuotas" si la vete usa precios por medio de pago
  const priceByPayment = useAuthStore((s) => s.tenant?.price_by_payment_method ?? false);
  // Solo mostrar "producto a granel" si la vete tiene fraccionados habilitado
  const hasFraccionados = useAuthStore((s) => s.tenant?.has_fraccionados ?? false);
  const createProduct = useCreateProduct();
  const updateProduct = useUpdateProduct();
  const createCategory = useCreateCategory();

  const {
    register,
    handleSubmit,
    watch,
    control,
    setValue,
    formState: { errors },
  } = useForm<ProductFormData>({
    resolver: zodResolver(productSchema),
    defaultValues: product
      ? {
          name: product.name,
          code: product.code || '',
          barcode: product.barcode || '',
          description: product.description || '',
          category_id: product.category_id?.toString() || '',
          supplier_id: product.supplier_id?.toString() || '',
          sale_price: product.sale_price?.toString() || '',
          list_price: product.list_price?.toString() || '',
          installment_price: product.installment_price?.toString() || '',
          cost_price: product.cost_price?.toString() || '',
          current_stock: product.current_stock?.toString() || '0',
          minimum_stock: product.minimum_stock?.toString() || '0',
          track_stock: product.track_stock,
          expiration_date: product.expiration_date || '',
          batch_number: product.batch_number || '',
          is_service: product.is_service,
          applicable_species: product.applicable_species?.split(',').filter(Boolean) || [],
          sell_by_ml: product.sell_by_ml || false,
          container_size_ml: product.container_size_ml?.toString() || '',
          price_per_ml: product.price_per_ml?.toString() || '',
          is_bulk: product.is_bulk || false,
        }
      : {
          track_stock: true,
          is_service: false,
          minimum_stock: '10',
          current_stock: '0',
          applicable_species: [],
          sell_by_ml: false,
        },
  });

  // Lector de código de barras: estando en la ficha, escanear en cualquier
  // lado completa el campo "Código de barras" (no hace falta pararse en él).
  useBarcodeScanner(
    (code) => {
      setValue('barcode', code, { shouldValidate: false, shouldDirty: true });
      toast.success(`Código de barras leído: ${code}`);
    },
    { preventInput: true }
  );

  const isService = watch('is_service');
  const trackStock = watch('track_stock');
  const costPrice = watch('cost_price');
  const salePrice = watch('sale_price');
  const listPrice = watch('list_price');
  const categoryId = watch('category_id');
  const sellByMl = watch('sell_by_ml');
  const isBulk = watch('is_bulk');
  // Fraccionado: sale de un producto a granel, no lleva stock propio
  const isFraccionado = !!(product?.source_product_id && product?.fraction_quantity);

  // Cuando el codigo de barras ya lo tiene otro producto, se ofrece liberarlo en vez de
  // dejar al usuario buscando a mano cual era y con que producto choca.
  const [barcodeConflict, setBarcodeConflict] = useState<{
    barcode: string;
    owner: Product;
    payload: any;
  } | null>(null);
  const [freeingBarcode, setFreeingBarcode] = useState(false);

  // Calculadora de compra a granel: el proveedor factura el TOTAL por los kg que trajo,
  // pero acá el costo se guarda por unidad (es el que heredan los fraccionados). Antes
  // se cargaba el total en "Costo" y quedaba mal el costo del kg y el de cada porción.
  const [bulkQuantity, setBulkQuantity] = useState('');
  const [bulkTotalPaid, setBulkTotalPaid] = useState('');

  const bulkUnitCost = (() => {
    const qty = parseFloat(bulkQuantity.replace(',', '.'));
    const total = parseFloat(bulkTotalPaid.replace(',', '.'));
    if (!qty || qty <= 0 || !total || total <= 0) return null;
    return Math.round((total / qty) * 100) / 100;
  })();

  const applyBulkUnitCost = (qty: string, total: string) => {
    const q = parseFloat(qty.replace(',', '.'));
    const t = parseFloat(total.replace(',', '.'));
    if (!q || q <= 0 || !t || t <= 0) return;
    setValue('cost_price', String(Math.round((t / q) * 100) / 100), { shouldDirty: true });
  };

  // Auto-asignar categoría "Servicios" cuando se marca is_service
  useEffect(() => {
    if (!isService || !categories) return;
    // Si ya tiene categoría asignada, no cambiar
    if (categoryId) return;

    const serviciosCat = categories.find(
      (c) => c.name.toLowerCase() === 'servicios' && c.is_active
    );
    if (serviciosCat) {
      setValue('category_id', serviciosCat.id.toString());
    } else {
      // Crear categoría "Servicios" automáticamente
      createCategory.mutate(
        { name: 'Servicios', description: 'Consultas, cirugías, peluquería y otros servicios' },
        {
          onSuccess: (newCat) => {
            setValue('category_id', newCat.id.toString());
            toast.success('Se creó la categoría "Servicios" automáticamente');
          },
        }
      );
    }
  }, [isService, categories]);

  // Obtener la categoría seleccionada para usar su configuración de precios
  const selectedCategory = categoryId
    ? categories?.find((c) => c.id.toString() === categoryId)
    : null;

  // Valores de fórmula de la categoría o defaults
  const priceFormula = (selectedCategory?.price_formula as PriceFormula) || 'multiply_round_up';
  const priceMultiplier = selectedCategory?.price_multiplier ?? 2.0;
  const marginPercentage = selectedCategory?.margin_percentage ?? 0;
  const priceRoundTo = selectedCategory?.price_round_to ?? 100;
  const listPriceMultiplier = selectedCategory?.list_price_multiplier ?? 1.10;

  // Calcular precios sugeridos basados en el costo y la fórmula de la categoría
  const suggestedSalePrice = costPrice
    ? calculateSalePrice(parseFloat(costPrice), priceFormula, priceMultiplier, marginPercentage, priceRoundTo)
    : null;
  // La sugerencia de tarjeta encadena sobre el efectivo: costo -> efectivo -> tarjeta.
  //
  // Se calcula sobre el efectivo CARGADO, que es la regla real ("tarjeta = efectivo +
  // 10%"), salvo que ese efectivo este por debajo del sugerido: ahi encadena desde el
  // sugerido. Sin eso las dos sugerencias hablaban de escenarios distintos y la de
  // tarjeta salia mas barata que la de efectivo, que no tiene sentido (costo 73.555 ->
  // sugerido efectivo 95.700, pero con 80.000 cargados sugeria tarjeta 88.000).
  const baseParaTarjeta = (() => {
    const cargado = salePrice ? parseFloat(salePrice) : 0;
    if (!cargado) return suggestedSalePrice;
    if (suggestedSalePrice && cargado < suggestedSalePrice) return suggestedSalePrice;
    return cargado;
  })();

  const suggestedListPrice = baseParaTarjeta
    ? calculateListPrice(baseParaTarjeta, listPriceMultiplier, priceRoundTo)
    : null;

  // Descripción de la fórmula para mostrar al usuario
  const formulaDescription = selectedCategory
    ? getFormulaDescription(priceFormula, priceMultiplier, marginPercentage, priceRoundTo)
    : null;

  // Auto-aplicar precio tarjeta SOLO al crear un producto nuevo. Al EDITAR no se toca,
  // para no pisar el precio de tarjeta/cuotas que cargó manualmente el usuario.
  useEffect(() => {
    if (product) return;  // editando: respetar el precio de tarjeta existente
    if (!salePrice || !selectedCategory) return;
    const salePriceNum = parseFloat(salePrice);
    if (!salePriceNum || salePriceNum <= 0) return;

    const newListPrice = calculateListPrice(salePriceNum, listPriceMultiplier, priceRoundTo);
    setValue('list_price', newListPrice.toString());
  }, [salePrice, categoryId]);

  // Aplicar precio efectivo sugerido
  const applySuggestedSalePrice = () => {
    if (suggestedSalePrice) {
      setValue('sale_price', suggestedSalePrice.toString());
    }
  };

  // Aplicar precio tarjeta sugerido
  const applySuggestedListPrice = () => {
    if (suggestedListPrice) {
      setValue('list_price', suggestedListPrice.toString());
    }
  };

  // Le saca el codigo de barras al producto que lo tenia y reintenta guardar este
  const handleFreeBarcode = async () => {
    if (!barcodeConflict) return;
    setFreeingBarcode(true);
    try {
      await updateProduct.mutateAsync({
        id: barcodeConflict.owner.id,
        data: { barcode: '' },
      });
      if (product) {
        await updateProduct.mutateAsync({ id: product.id, data: barcodeConflict.payload });
        toast.success('Producto actualizado');
      } else {
        await createProduct.mutateAsync(barcodeConflict.payload);
        toast.success('Producto creado');
      }
      setBarcodeConflict(null);
      onSuccess();
    } catch (e: any) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'No se pudo liberar el código de barras');
    } finally {
      setFreeingBarcode(false);
    }
  };

  const onSubmit = async (data: ProductFormData) => {
    const payload = {
      name: data.name,
      // Vacio se manda como null, NO como undefined: undefined no viaja en el JSON y el
      // backend deja el valor anterior, asi que borrar un codigo desde la ficha no hacia
      // nada (quedaba ocupado y seguia dando "codigo duplicado" en otro producto).
      code: data.code?.trim() ? data.code.trim() : null,
      barcode: data.barcode?.trim() ? data.barcode.trim() : null,
      description: data.description || undefined,
      category_id: data.category_id ? Number(data.category_id) : undefined,
      supplier_id: data.supplier_id ? Number(data.supplier_id) : undefined,
      sale_price: data.sale_price ? Number(data.sale_price) : undefined,  // Precio efectivo
      list_price: data.list_price ? Number(data.list_price) : undefined,  // Precio tarjeta
      installment_price: data.installment_price ? Number(data.installment_price) : undefined,  // Precio crédito en cuotas
      cost_price: data.cost_price ? Number(data.cost_price) : undefined,
      current_stock: Number(data.current_stock),
      minimum_stock: Number(data.minimum_stock),
      track_stock: data.track_stock,
      expiration_date: data.expiration_date || undefined,
      batch_number: data.batch_number || undefined,
      is_service: data.is_service,
      applicable_species: data.applicable_species.length > 0 ? data.applicable_species.join(',') : undefined,
      // Venta por ml
      sell_by_ml: data.sell_by_ml,
      container_size_ml: data.container_size_ml ? Number(data.container_size_ml) : undefined,
      price_per_ml: data.price_per_ml ? Number(data.price_per_ml) : undefined,
      // Producto a granel (se puede fraccionar)
      is_bulk: data.is_bulk,
    };

    try {
      if (product) {
        await updateProduct.mutateAsync({ id: product.id, data: payload });
        toast.success('Producto actualizado');
      } else {
        await createProduct.mutateAsync(payload);
        toast.success('Producto creado');
      }
      onSuccess();
    } catch (error: any) {
      console.error('Error saving product:', error);
      const detail = error?.response?.data?.detail;
      const status = error?.response?.status;

      // Choque de codigo de barras: ofrecer liberarlo del producto que lo tiene
      const barcode = (payload.barcode || '').trim();
      if (status === 409 && barcode && typeof detail === 'string' && detail.includes(barcode)) {
        const owner = await productsService.getProductByBarcode(barcode);
        if (owner && owner.id !== product?.id) {
          setBarcodeConflict({ barcode, owner, payload });
          return;
        }
      }

      if (typeof detail === 'string' && detail) {
        // Mensaje concreto del backend (ej: código o código de barras duplicado)
        toast.error(detail);
      } else if (status === 409) {
        toast.error('Ya existe un producto con ese código o código de barras. Cambialo e intentá de nuevo.');
      } else {
        toast.error('No se pudo guardar el producto. Revisá los datos e intentá de nuevo.');
      }
    }
  };

  const categoryOptions = [
    { value: '', label: 'Sin categoría' },
    ...(categories?.map((c) => ({ value: c.id.toString(), label: c.name })) || []),
  ];

  const supplierOptions = [
    { value: '', label: 'Sin proveedor' },
    ...(suppliers?.map((s) => ({ value: s.id.toString(), label: s.name })) || []),
  ];

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Input
          label="Nombre *"
          {...register('name')}
          error={errors.name?.message}
        />
        <Input label="Código" {...register('code')} />
      </div>

      <Input
        label="Código de barras"
        placeholder="Escaneá con el lector o ingresá el número"
        {...register('barcode')}
        onKeyDown={(e) => {
          // Evitar que el Enter del lector envíe el formulario: solo completa el campo.
          if (e.key === 'Enter') e.preventDefault();
        }}
      />

      <Input label="Descripción" {...register('description')} />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Select
          label="Categoría"
          options={categoryOptions}
          {...register('category_id')}
        />
        <Select
          label="Proveedor"
          options={supplierOptions}
          {...register('supplier_id')}
        />
      </div>

      <div className="flex items-center gap-4">
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            {...register('is_service')}
            className="rounded border-gray-300 text-primary-600"
          />
          <span className="text-sm">Es un servicio</span>
        </label>
      </div>

      {/* Especies aplicables */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Especies aplicables <span className="text-gray-400 font-normal">(opcional)</span>
        </label>
        <div className="flex flex-wrap gap-3">
          <Controller
            name="applicable_species"
            control={control}
            render={({ field }) => (
              <>
                {SPECIES_OPTIONS.map((species) => (
                  <label
                    key={species.value}
                    className="flex items-center gap-2 cursor-pointer bg-gray-50 px-3 py-2 rounded-lg hover:bg-gray-100 transition-colors"
                  >
                    <input
                      type="checkbox"
                      checked={field.value.includes(species.value)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          field.onChange([...field.value, species.value]);
                        } else {
                          field.onChange(field.value.filter((v: string) => v !== species.value));
                        }
                      }}
                      className="rounded border-gray-300 text-primary-600"
                    />
                    <span className="text-sm">{species.label}</span>
                  </label>
                ))}
              </>
            )}
          />
        </div>
        <p className="text-xs text-gray-500 mt-1">
          Dejar vacío si aplica a todas las especies
        </p>
      </div>

      {/* Sección de precios */}
      <div className="bg-gray-50 p-4 rounded-lg space-y-4">
        <h3 className="font-medium text-gray-700 flex items-center gap-2">
          <Calculator className="h-4 w-4" />
          Precios
        </h3>
        {/* Compra a granel: de la factura del proveedor sale el costo por unidad */}
        {isBulk && !isService && (
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 space-y-3">
            <p className="text-sm text-blue-800">
              <strong>Compra a granel:</strong> poné cuánto compraste y el <strong>precio total</strong> que
              pagaste. Te calcula el costo por unidad, que es el que heredan los fraccionados.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Input
                label="Cantidad comprada (kg, unidades…)"
                type="text"
                inputMode="decimal"
                placeholder="Ej: 22"
                value={bulkQuantity}
                onChange={(e) => {
                  setBulkQuantity(e.target.value);
                  applyBulkUnitCost(e.target.value, bulkTotalPaid);
                }}
              />
              <Input
                label="Precio total pagado ($)"
                type="text"
                inputMode="decimal"
                placeholder="Ej: 30000"
                value={bulkTotalPaid}
                onChange={(e) => {
                  setBulkTotalPaid(e.target.value);
                  applyBulkUnitCost(bulkQuantity, e.target.value);
                }}
              />
            </div>
            {bulkUnitCost !== null && (
              <p className="text-sm font-medium text-blue-900">
                Costo por unidad: ${bulkUnitCost.toLocaleString('es-AR')} — se cargó abajo en "Costo".
              </p>
            )}
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-start">
          <div className="flex flex-col">
            <Controller
              control={control}
              name="cost_price"
              render={({ field }) => (
                <Input
                  label="Costo"
                  type="text"
                  inputMode="decimal"
                  placeholder="0"
                  value={formatMiles(field.value)}
                  onChange={(e) => field.onChange(parseToCanonical(e.target.value))}
                  onBlur={field.onBlur}
                />
              )}
            />
            <div className="h-6" /> {/* Spacer para alinear */}
          </div>
          <div className="flex flex-col">
            <Controller
              control={control}
              name="sale_price"
              render={({ field }) => (
                <Input
                  label="Efectivo"
                  type="text"
                  inputMode="decimal"
                  placeholder="0"
                  value={formatMiles(field.value)}
                  onChange={(e) => field.onChange(parseToCanonical(e.target.value))}
                  onBlur={field.onBlur}
                  error={errors.sale_price?.message}
                />
              )}
            />
            <div className="h-6">
              {suggestedSalePrice && (!salePrice || parseFloat(salePrice) !== suggestedSalePrice) && (
                <button
                  type="button"
                  onClick={applySuggestedSalePrice}
                  className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1"
                >
                  <Calculator className="h-3 w-3" />
                  Sugerido: ${suggestedSalePrice.toLocaleString('es-AR')}
                </button>
              )}
            </div>
          </div>
          <div className="flex flex-col">
            <Controller
              control={control}
              name="list_price"
              render={({ field }) => (
                <Input
                  label="Tarjeta"
                  type="text"
                  inputMode="decimal"
                  placeholder="0"
                  value={formatMiles(field.value)}
                  onChange={(e) => field.onChange(parseToCanonical(e.target.value))}
                  onBlur={field.onBlur}
                />
              )}
            />
            <div className="h-6">
              {/* Se muestra igual que la de efectivo: mientras el precio cargado no
                  coincida con el sugerido. Antes pedia que hubiera efectivo cargado, asi
                  que al crear un producto con solo el costo no aparecia ninguna. */}
              {suggestedListPrice && (!listPrice || parseFloat(listPrice) !== suggestedListPrice) && (
                <button
                  type="button"
                  onClick={applySuggestedListPrice}
                  className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1"
                >
                  <Calculator className="h-3 w-3" />
                  Sugerido: ${suggestedListPrice.toLocaleString('es-AR')}
                </button>
              )}
            </div>
          </div>
        </div>
        <p className="text-xs text-gray-500">
          Ingresá el costo y hacé click en los precios sugeridos para aplicarlos, o editá manualmente.
          {formulaDescription && (
            <span className="block mt-1 text-blue-600">
              Fórmula de {selectedCategory?.name}: {formulaDescription}
            </span>
          )}
        </p>

        {/* Precio crédito en cuotas: solo si la vete usa precios por medio de pago */}
        {priceByPayment && (
          <div className="pt-2 border-t border-gray-200">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Controller
                control={control}
                name="installment_price"
                render={({ field }) => (
                  <Input
                    label="Crédito en cuotas"
                    type="text"
                    inputMode="decimal"
                    placeholder="0"
                    value={formatMiles(field.value)}
                    onChange={(e) => field.onChange(parseToCanonical(e.target.value))}
                    onBlur={field.onBlur}
                  />
                )}
              />
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Se usa cuando el pago es tarjeta de crédito en cuotas. Si lo dejás vacío, se cobra al precio de tarjeta.
            </p>
          </div>
        )}
      </div>

      {/* Sección de venta por ml - solo para productos (no servicios) */}
      {!isService && (
        <div className="bg-blue-50 p-4 rounded-lg space-y-4 border border-blue-200">
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              {...register('sell_by_ml')}
              className="rounded border-blue-300 text-blue-600"
            />
            <span className="text-sm font-medium text-blue-800">Vender por ml (productos líquidos dosificados)</span>
          </div>

          {sellByMl && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="Tamaño envase (ml)"
                type="number"
                step="0.01"
                placeholder="Ej: 250"
                {...register('container_size_ml')}
              />
              <Input
                label="Precio por ml"
                type="number"
                step="0.0001"
                placeholder="Ej: 50.00"
                {...register('price_per_ml')}
              />
            </div>
          )}

          {sellByMl && (
            <p className="text-xs text-blue-700">
              Al vender, el usuario ingresa los ml utilizados y el sistema calcula el precio.
              El stock se descuenta en ml. Ejemplo: si usas 3ml a $50/ml = $150.
            </p>
          )}
        </div>
      )}

      {/* Producto a granel - solo si la vete tiene fraccionados habilitado */}
      {!isService && hasFraccionados && (
        <div className="bg-amber-50 p-4 rounded-lg space-y-1 border border-amber-200">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              {...register('is_bulk')}
              className="rounded border-amber-300 text-amber-600"
            />
            <span className="text-sm font-medium text-amber-800">Producto a granel (se puede fraccionar)</span>
          </label>
          <p className="text-xs text-amber-700">
            Marcalo si de este producto vas a sacar porciones. Ej: "Alimento 20 kg" del que hacés
            fraccionados de 1 kg y ½ kg. Cargá el stock y el costo en la misma unidad que vas a fraccionar
            (ej. stock 20, costo por kg). Solo los productos a granel aparecen como origen en Fraccionados.
          </p>
        </div>
      )}

      {!isService && (
        <>
          {isFraccionado ? (
            /* Un fraccionado nunca lleva stock propio: al venderlo se descuentan sus
               gramos del producto a granel. El backend fuerza track_stock=false, asi
               que dejar el tilde disponible solo confundia (se destildaba solo). */
            <div className="bg-teal-50 border border-teal-200 rounded-lg p-3">
              <p className="text-sm font-medium text-teal-800">
                El stock lo controla el producto a granel
              </p>
              <p className="text-xs text-teal-700 mt-0.5">
                Este es un producto fraccionado{product?.source_product_name
                  ? ` de ${product.source_product_name}`
                  : ''}: no lleva stock propio. Al venderlo se descuentan
                {product?.fraction_quantity
                  ? ` ${Math.round(Number(product.fraction_quantity) * 1000)} g`
                  : ' los gramos del paquete'}{' '}
                del stock del granel, y las unidades disponibles se calculan solas.
              </p>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                {...register('track_stock')}
                className="rounded border-gray-300 text-primary-600"
              />
              <span className="text-sm">Controlar stock</span>
            </div>
          )}

          {trackStock && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="Stock actual"
                type="number"
                step="any"
                {...register('current_stock')}
              />
              <Input
                label="Stock mínimo (alerta)"
                type="number"
                step="any"
                {...register('minimum_stock')}
              />
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Fecha de vencimiento"
              type="date"
              {...register('expiration_date')}
            />
            <Input label="Número de lote" {...register('batch_number')} />
          </div>
        </>
      )}

      {/* El codigo de barras lo tiene otro producto: se ofrece liberarlo */}
      {barcodeConflict && (
        <div className="bg-amber-50 border border-amber-300 rounded-lg p-4 space-y-3">
          <div>
            <p className="text-sm font-medium text-amber-900">
              El código {barcodeConflict.barcode} lo está usando otro producto
            </p>
            <p className="text-sm text-amber-800 mt-1">
              Lo tiene <strong>{barcodeConflict.owner.name}</strong>
              {barcodeConflict.owner.code ? ` (${barcodeConflict.owner.code})` : ''}. Si ese
              producto ya no se usa, podés quitarle el código y asignárselo a este. El otro
              producto no se borra: sólo se queda sin código de barras.
            </p>
          </div>
          <div className="flex flex-wrap justify-end gap-2">
            <Button type="button" variant="secondary" onClick={() => setBarcodeConflict(null)}>
              Cancelar
            </Button>
            <Button type="button" onClick={handleFreeBarcode} loading={freeingBarcode}>
              Quitárselo y usarlo acá
            </Button>
          </div>
        </div>
      )}

      <div className="flex justify-end gap-2 pt-4">
        <Button type="submit" loading={createProduct.isPending || updateProduct.isPending}>
          {product ? 'Guardar Cambios' : 'Crear Producto'}
        </Button>
      </div>
    </form>
  );
}
